import { bufferedParserFetchEffect } from './http';
import { execFile } from 'node:child_process';
import { mkdtemp, readFile, rm, writeFile } from 'node:fs/promises';
import { tmpdir } from 'node:os';
import { join } from 'node:path';

import { Effect, Layer, Option } from 'effect';
import { PDFDocument } from 'pdf-lib';

import {
  DocumentSource,
  makeParserDependencyError,
  PageSource,
  ParserHttp,
  ParserSecrets,
  ParserTracer,
  PdfTextLayer,
  makeLangfuseParserTracerService,
  type PageImage,
  type PageRegionClip,
  type ParseDocumentRef,
  type ParserDependencyError,
  type PdfTextLayerPage,
  type TextItem,
} from '../parser-host';
import { gcsObjectBytesEffect, parseGcsServiceAccount } from './gcs';
import { cropPdfPageToRegionEffect } from './pdf-crop';

export interface ParserHostNodeOptions {
  readonly allowedHosts: readonly string[];
  readonly secrets?: Readonly<Record<string, string | undefined>>;
  readonly rasterCommand?: string;
  readonly fetch?: typeof globalThis.fetch;
}

function failure(
  service: 'http' | 'page-source' | 'text-layer',
  operation: string,
  code: string,
  retryable: boolean,
  cause?: unknown,
) {
  return makeParserDependencyError({ service, operation, code, retryable }, cause);
}

function isAllowedHost(url: URL, allowedHosts: readonly string[]): boolean {
  return allowedHosts.some((host) => {
    const normalized = host.trim().toLowerCase();
    if (normalized.length === 0) return false;
    const hostname = url.hostname.toLowerCase();
    if (normalized.startsWith('*.')) {
      const suffix = normalized.slice(2);
      return hostname === suffix || hostname.endsWith(`.${suffix}`);
    }
    return hostname === normalized;
  });
}

async function splitPdf(bytes: Uint8Array, page: number): Promise<Uint8Array> {
  const source = await PDFDocument.load(bytes, { ignoreEncryption: true });
  if (!Number.isInteger(page) || page < 1 || page > source.getPageCount()) {
    throw new RangeError('page is outside the document');
  }
  const output = await PDFDocument.create();
  const [copied] = await output.copyPages(source, [page - 1]);
  output.addPage(copied);
  return output.save();
}

function pngDimensions(bytes: Uint8Array): { width: number; height: number } {
  if (bytes.length < 24 || bytes[0] !== 0x89 || bytes[1] !== 0x50) {
    throw new Error('invalid PNG output');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return { width: view.getUint32(16), height: view.getUint32(20) };
}

function jpegDimensions(bytes: Uint8Array): { width: number; height: number } {
  let offset = 2;
  while (offset + 8 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = bytes[offset + 1] ?? 0;
    const length = ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0);
    if (marker >= 0xc0 && marker <= 0xc3) {
      return {
        height: ((bytes[offset + 5] ?? 0) << 8) | (bytes[offset + 6] ?? 0),
        width: ((bytes[offset + 7] ?? 0) << 8) | (bytes[offset + 8] ?? 0),
      };
    }
    offset += Math.max(length + 2, 2);
  }
  throw new Error('invalid JPEG output');
}

function run(command: string, args: readonly string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    execFile(command, [...args], { maxBuffer: 8 * 1024 * 1024 }, (error) => {
      if (error) reject(error);
      else resolve();
    });
  });
}

/** Shared Node raster primitive used by the Node host and the pdfdom adapter. */
export async function renderPdfPageWithPdftoppm(options: {
  readonly bytes: Uint8Array;
  readonly page: number;
  readonly dpi: number;
  readonly format: 'png' | 'jpeg';
  readonly command?: string;
}): Promise<PageImage> {
  const directory = await mkdtemp(join(tmpdir(), 'okrapdf-parser-'));
  const inputPath = join(directory, 'document.pdf');
  const outputPrefix = join(directory, 'page');
  const extension = options.format === 'png' ? 'png' : 'jpg';
  const outputPath = `${outputPrefix}.${extension}`;
  try {
    await writeFile(inputPath, options.bytes);
    await run(options.command ?? 'pdftoppm', [
      `-${options.format === 'png' ? 'png' : 'jpeg'}`,
      '-singlefile',
      '-f',
      String(options.page),
      '-l',
      String(options.page),
      '-r',
      String(options.dpi),
      inputPath,
      outputPrefix,
    ]);
    const bytes = new Uint8Array(await readFile(outputPath));
    const dimensions = options.format === 'png' ? pngDimensions(bytes) : jpegDimensions(bytes);
    return {
      bytes,
      mimeType: options.format === 'png' ? 'image/png' : 'image/jpeg',
      ...dimensions,
    };
  } finally {
    await rm(directory, { recursive: true, force: true });
  }
}

interface UnpdfTextPage {
  readonly getViewport: (options: { readonly scale: number }) => {
    readonly width: number;
    readonly height: number;
  };
  readonly getTextContent: () => Promise<{ readonly items: readonly unknown[] }>;
}

interface UnpdfDocument {
  readonly getPage: (page: number) => Promise<UnpdfTextPage>;
  readonly destroy?: () => Promise<void>;
}

function decodeTextItem(item: unknown): TextItem | undefined {
  if (typeof item !== 'object' || item === null || !('str' in item)) return undefined;
  const candidate = item as {
    readonly str?: unknown;
    readonly width?: unknown;
    readonly height?: unknown;
    readonly transform?: unknown;
  };
  if (typeof candidate.str !== 'string') return undefined;
  return {
    str: candidate.str,
    ...(typeof candidate.width === 'number' ? { width: candidate.width } : {}),
    ...(typeof candidate.height === 'number' ? { height: candidate.height } : {}),
    ...(Array.isArray(candidate.transform) &&
    candidate.transform.every((value) => typeof value === 'number')
      ? { transform: candidate.transform }
      : {}),
  };
}

async function extractTextPage(bytes: Uint8Array, page: number): Promise<PdfTextLayerPage> {
  const unpdf = await import('unpdf');
  const proxy = (await unpdf.getDocumentProxy(bytes)) as unknown as UnpdfDocument;
  try {
    const pdfPage = await proxy.getPage(page);
    const viewport = pdfPage.getViewport({ scale: 1 });
    const content = await pdfPage.getTextContent();
    return {
      items: content.items.flatMap((item) => {
        const decoded = decodeTextItem(item);
        return decoded ? [decoded] : [];
      }),
      width: viewport.width,
      height: viewport.height,
    };
  } finally {
    await proxy.destroy?.();
  }
}

export function parserHostNode(
  options: ParserHostNodeOptions,
): Layer.Layer<
  ParserHttp | PageSource | PdfTextLayer | ParserSecrets | DocumentSource | ParserTracer
> {
  const fetchImplementation = options.fetch ?? globalThis.fetch;
  const http = ParserHttp.of({
    fetch: (input, init) => {
      let url: URL;
      try {
        url = input instanceof URL ? input : new URL(input);
      } catch (cause) {
        return Effect.fail(failure('http', 'fetch', 'invalid_url', false, cause));
      }
      if (!isAllowedHost(url, options.allowedHosts)) {
        return Effect.fail(failure('http', 'fetch', 'host_not_allowed', false));
      }
      return bufferedParserFetchEffect(fetchImplementation, url, init, (cause, stage) =>
        failure(
          'http',
          'fetch',
          stage === 'body' ? 'response_read_failed' : 'request_failed',
          stage === 'request' && init?.signal?.aborted !== true,
          cause,
        ),
      );
    },
  });
  const resolveBytes = (
    document: ParseDocumentRef,
  ): Effect.Effect<Uint8Array, ParserDependencyError> => {
    if (document.kind !== 'storage') return Effect.succeed(document.bytes);
    if (document.provider !== 'gcs') {
      return Effect.fail(
        makeParserDependencyError({
          service: 'document-source',
          operation: 'bytes',
          code: 'provider_unsupported',
          retryable: false,
        }),
      );
    }
    return Effect.gen(function* () {
      const raw = options.secrets?.GCS_SERVICE_ACCOUNT_KEY ?? process.env.GCS_SERVICE_ACCOUNT_KEY;
      if (!raw) {
        return yield* makeParserDependencyError({
          service: 'document-source',
          operation: 'gcs-auth',
          code: 'credentials_unavailable',
          retryable: false,
        });
      }
      const account = yield* Effect.try({
        try: () => parseGcsServiceAccount(raw),
        catch: (cause) =>
          makeParserDependencyError(
            {
              service: 'document-source',
              operation: 'gcs-auth',
              code: 'credentials_invalid',
              retryable: false,
            },
            cause,
          ),
      });
      return yield* gcsObjectBytesEffect(
        (input, init) => {
          let url: URL;
          try {
            url = input instanceof URL ? input : new URL(input);
          } catch (cause) {
            return Effect.fail(failure('http', 'fetch', 'invalid_url', false, cause));
          }
          if (!isAllowedHost(url, options.allowedHosts)) {
            return Effect.fail(failure('http', 'fetch', 'host_not_allowed', false));
          }
          return Effect.tryPromise({
            try: () => fetchImplementation(url, init),
            catch: (cause) => failure('http', 'fetch', 'request_failed', true, cause),
          });
        },
        account,
        document.bucket,
        document.key,
      );
    });
  };
  const pageSource = PageSource.of({
    countPages: (document) =>
      document.pageCount !== undefined
        ? Effect.succeed(document.pageCount)
        : resolveBytes(document).pipe(
            Effect.flatMap((bytes) =>
              Effect.tryPromise({
                try: async () =>
                  (await PDFDocument.load(bytes, { ignoreEncryption: true })).getPageCount(),
                catch: (cause) =>
                  failure('page-source', 'count-pages', 'invalid_pdf', false, cause),
              }),
            ),
          ),
    pagePdf: (document, page, clip?: PageRegionClip) =>
      resolveBytes(document).pipe(
        Effect.flatMap((bytes) =>
          Effect.tryPromise({
            try: () => splitPdf(bytes, page),
            catch: (cause) => failure('page-source', 'page-pdf', 'invalid_page', false, cause),
          }),
        ),
        Effect.flatMap((bytes) =>
          clip?.region === undefined
            ? Effect.succeed(bytes)
            : cropPdfPageToRegionEffect(bytes, clip.region),
        ),
      ),
    pageImage: (document, page, raster, clip?: PageRegionClip) =>
      (clip?.region === undefined
        ? Effect.map(resolveBytes(document), (bytes) => ({ bytes, page }))
        : resolveBytes(document).pipe(
            Effect.flatMap((bytes) =>
              Effect.tryPromise({
                try: () => splitPdf(bytes, page),
                catch: (cause) => failure('page-source', 'page-pdf', 'invalid_page', false, cause),
              }),
            ),
            Effect.flatMap((bytes) => cropPdfPageToRegionEffect(bytes, clip.region!)),
            Effect.map((bytes) => ({ bytes, page: 1 })),
          )
      ).pipe(
        Effect.flatMap(({ bytes, page: rasterPage }) =>
          Effect.tryPromise({
            try: () =>
              renderPdfPageWithPdftoppm({
                bytes,
                page: rasterPage,
                ...raster,
                ...(options.rasterCommand === undefined ? {} : { command: options.rasterCommand }),
              }),
            catch: (cause) => failure('page-source', 'page-image', 'raster_failed', true, cause),
          }),
        ),
      ),
  });
  const textLayer = PdfTextLayer.of({
    extractPage: (document, page) =>
      resolveBytes(document).pipe(
        Effect.flatMap((bytes) =>
          Effect.tryPromise({
            try: () => extractTextPage(bytes, page),
            catch: (cause) => failure('text-layer', 'extract-page', 'extract_failed', false, cause),
          }),
        ),
      ),
  });
  const secrets = ParserSecrets.of({
    get: (name) =>
      Effect.sync(() => Option.fromNullishOr(options.secrets?.[name] ?? process.env[name])),
  });
  const documentSource = DocumentSource.of({ bytes: resolveBytes });
  const tracer = ParserTracer.of(
    makeLangfuseParserTracerService({
      getSecret: (name) => options.secrets?.[name] ?? process.env[name],
      fetch: fetchImplementation,
    }),
  );
  return Layer.mergeAll(
    Layer.succeed(ParserHttp, http),
    Layer.succeed(PageSource, pageSource),
    Layer.succeed(PdfTextLayer, textLayer),
    Layer.succeed(ParserSecrets, secrets),
    Layer.succeed(DocumentSource, documentSource),
    Layer.succeed(ParserTracer, tracer),
  );
}
